import { UserAccessDetails, UserAccountDetails } from "@/models/user-accounts";
import { BehaviorSubject } from "rxjs";
import axios from "@/services/axios";
import { CardData } from "@/pinia/useAccountManagementStore";
import { AccountManagementTabs } from "@/helpers/subMenus";
import { STATS_URL } from "@/models/stats-texts";

class UserAccountsService {
  getUserProfileDetails() {
    return axios.get("/api/account-management/user-accounts/user-profile");
  }

  getUserAccounts(
    pageIndex: number,
    pageSize: number,
    searchText?: string,
    sortField?: string,
    sortOrder?: string,
    status?: string,
  ): Promise<UserAccountDetails> {
    const dir =
      sortOrder === "descend" ? sortField + ",DESC" : sortField + ",ASC";
    const sort = sortField && sortOrder != "none" ? dir : "";

    let param: any = {
      filter: searchText,
      page: pageIndex,
      size: pageSize,
      sort,
    };
    if (status) {
      param["userStatus"] = status;
    }
    return axios.get("/api/account-management/user-accounts", {
      params: param,
    });
  }

  addUserAccounts(requestBody: any): Promise<any> {
    return axios.post("/api/account-management/user-accounts", requestBody);
  }

  updateUserAccount(userAccount: any): Promise<any> {
    return axios.put(`/api/account-management/user-accounts`, userAccount);
  }

  deleteUserAccount(username: string | null): Promise<boolean> {
    return axios.delete(`/api/account-management/user-accounts/${username}`);
  }

  updateUserAccountsPassword(userAccount: any): Promise<any> {
    return axios.put(
      `/api/account-management/user-accounts/update-password`,
      userAccount,
    );
  }

  exportSampleFile(fileTypes: string): Promise<any> {
    const headers =
      fileTypes === "CSV"
        ? { Accept: "text/csv; charset=utf-8" }
        : {
            "Content-Disposition": "attachment; filename=template.xlsx",

            "Content-Type":
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          };

    const responseType = fileTypes === "CSV" ? "text" : "arraybuffer";

    return axios.get(`/api/account-management/user-accounts/export-sample`, {
      params: {
        fileTypes,
      },

      headers: { ...headers },

      responseType,
    });
  }

  uploadSampleFile(file: any) {
    const formData = new FormData();
    formData.append("file", file);

    return axios.post(`/api/account-management/user-accounts/import`, formData);
  }

  resendVerification(requestBody: any): Promise<any> {
    return axios.get(
      `/api/account-management/user-accounts/send-verification-link/${requestBody.id}`,
      {
        params: {
          addressType: requestBody.addressType,
        },
      },
    );
  }

  fetchStatsData(cardDetails: CardData) {
    const statsUrl: string =
      cardDetails.name == AccountManagementTabs.USER_ACCOUNTS
        ? STATS_URL.USER_ACCOUNT
        : STATS_URL.GROUP_ACCESS;
    const url =
      cardDetails.name == AccountManagementTabs.MSISDN_ACCESS
        ? STATS_URL.MSISDN_ACCESS
        : statsUrl;
    return axios.get(url);
  }

  getUserProfile(): Promise<any> {
    return axios.get("/api/account-management/user-profiles");
  }

  setPrimaryAddress(payload: {
    primaryAddress: string;
    secondaryAddress: string;
  }): Promise<any> {
    return axios.post(`/api/account-management/user-profiles`, payload);
  }

  getUserAccountUserProfile(): Promise<any> {
    return axios.get("/api/account-management/user-accounts/user-profile");
  }

  getBanners() {
    return axios.get("/api/account-management/banners");
  }

  dismissBanner(bannerId: string) {
    return axios.post(`/api/account-management/banners/${bannerId}/dismiss`);
  }

  private readonly userAccessDetails = new BehaviorSubject<UserAccessDetails>(
    {},
  );
  readonly userAccessDetails$ = this.userAccessDetails.asObservable();
}
export const userAccountsService = new UserAccountsService();
